import { useEffect, useState } from "react";
import { t } from "i18next";
import { Avatar, Button, Modal, Space, Tag, Tooltip, Typography } from "antd";
import {
    UserOutlined,
    SendOutlined,
    FileTextOutlined,
    MedicineBoxOutlined,
    ApiOutlined,
    EyeOutlined,
    CloudSyncOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    ThunderboltOutlined
} from "@ant-design/icons";
import moment from "moment";
import DataGrid from "../../core/components/data-grid/DataGrid";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import Helper from "../../core/Helper";
import ApiService from "../../core/service/ApiService";
import EnabizOperationDetailModal from "./EnabizOperationDetailModal";
import EnabizTeletipStatusModal from "./EnabizTeletipStatusModal";
import EnabizRemoveKosModal from "./EnabizRemoveKosModal";

const { Text } = Typography;

function BlockersTooltip({ blockers, fallback }) {
    if (!blockers || blockers.length === 0) {
        return fallback || null
    }
    return (
        <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('Reason not sent')}:</div>
            <ul style={{ paddingLeft: 16, margin: 0 }}>
                {blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
        </div>
    )
}

function StatusTag({ status, sentAt, error, blockers, neverSentLabel }) {
    if (status === true) {
        return (
            <Tooltip title={sentAt ? moment(sentAt).format('DD.MM.YYYY HH:mm:ss') : null}>
                <Tag icon={<CheckCircleOutlined />} color="success">{t('Success')}</Tag>
            </Tooltip>
        )
    }
    if (status === false) {
        return (
            <Tooltip title={error || t('Failed')}>
                <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>
            </Tooltip>
        )
    }
    return (
        <Tooltip title={<BlockersTooltip blockers={blockers} />}>
            <Tag>{neverSentLabel || t('Not Sent')}</Tag>
        </Tooltip>
    )
}

export default function EnabizOperationDataGrid() {
    const { queryModel, setDataGridProps, setData, setPaginationResult, refreshDataGrid } = useDataGrid();

    const [detail, setDetail] = useState({ open: false, messageId: null });
    const [teletipStatus, setTeletipStatus] = useState({ open: false, appointmentId: null, accessionNumber: null });
    const [removeKos, setRemoveKos] = useState({ open: false, row: null });

    const handleSend = (action, row) => {
        const titles = {
            ris: t('Send RIS Teletip Job?'),
            request: t('Send Request HL7?'),
            report: t('Send Report HL7?'),
            risHl7: t('Force send RIS HL7?'),
        }
        const contents = {
            risHl7: t('All preconditions will be skipped. Continue?'),
        }
        Modal.confirm({
            title: titles[action],
            content: (
                <>
                    <div>{`${row.patientName} ${row.patientLastname} - ${row.operationName} (${row.accessionNumber || '-'})`}</div>
                    {contents[action] && <div style={{ marginTop: 8, color: '#d48806' }}>{contents[action]}</div>}
                </>
            ),
            okText: t('Send'),
            cancelText: t('Cancel'),
            onOk: async () => {
                let res
                if (action === 'ris') res = await ApiService.EnabizOperation.SendRisJob(row.id)
                else if (action === 'request') res = await ApiService.EnabizOperation.SendRequestHl7(row.id)
                else if (action === 'report') res = await ApiService.EnabizOperation.SendReportHl7(row.id)
                else if (action === 'risHl7') res = await ApiService.EnabizOperation.SendRisHl7(row.id)
                if (res && res.success) refreshDataGrid()
            }
        })
    }

    useEffect(() => {
        setDataGridProps({
            rowKey: 'id',
            columns: [
                {
                    key: 'appointmentId', dataIndex: 'appointmentId', title: t('Apt Id'), width: 90,
                    render: (val, row) => (
                        <div>
                            <Text strong>#{val}</Text>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>{moment(row.appointmentStartDate).format('DD.MM.YYYY HH:mm')}</div>
                        </div>
                    )
                },
                {
                    key: 'patientName', dataIndex: 'patientName', title: t('Patient'),
                    render: (_, row) => (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Avatar size={24} icon={<UserOutlined />} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <Text strong>{row.patientName} {row.patientLastname}</Text>
                                <Text type="secondary" style={{ fontSize: 11 }}>{row.patientCustomId || '-'}</Text>
                            </div>
                        </div>
                    )
                },
                {
                    key: 'operationName', dataIndex: 'operationName', title: t('Operation'),
                    render: (val, row) => (
                        <Space size={4}>
                            <MedicineBoxOutlined style={{ color: '#1890ff' }} />
                            <div>
                                <div>{val}</div>
                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                    {row.modality || '-'} {row.sutCode ? `• ${row.sutCode}` : ''}
                                </div>
                            </div>
                        </Space>
                    )
                },
                {
                    key: 'accessionNumber', dataIndex: 'accessionNumber', title: t('Accession No'), width: 140,
                    render: (val) => val ? <Text code>{val}</Text> : '-'
                },
                {
                    key: 'unitName', dataIndex: 'unitName', title: t('Unit'), width: 130,
                    render: (val) => <Text style={{ fontSize: 12 }}>{val}</Text>
                },
                {
                    key: 'institutionName', dataIndex: 'institutionName', title: t('Institution'), width: 140,
                    render: (val) => <Tooltip title={val}><Text ellipsis style={{ maxWidth: 130, fontSize: 12 }}>{val}</Text></Tooltip>
                },
                {
                    key: 'risHl7Sent', dataIndex: 'risHl7Sent', title: t('RIS HL7'), width: 130,
                    render: (val, row) => val
                        ? <Tooltip title={(
                            <div style={{ whiteSpace: 'pre-wrap' }}>
                                {row.risHl7SentDate ? moment(row.risHl7SentDate).format('DD.MM.YYYY HH:mm:ss') : null}
                                {row.risHl7AckMessage ? `\nACK: ${row.risHl7AckMessage}` : ''}
                            </div>
                        )}>
                            <Tag icon={<CheckCircleOutlined />} color="success">{t('Sent')}</Tag>
                        </Tooltip>
                        : <Tooltip title={<BlockersTooltip blockers={row.risHl7Blockers} />}>
                            <Tag>{t('Not Sent')}</Tag>
                        </Tooltip>
                },
                {
                    key: 'risJobSuccess', dataIndex: 'risJobSuccess', title: t('RIS KOS'), width: 130,
                    render: (val, row) => (
                        <StatusTag
                            status={val}
                            sentAt={row.risJobSentDate}
                            error={row.risJobError}
                            blockers={row.risJobBlockers}
                        />
                    )
                },
                {
                    key: 'requestHl7Success', dataIndex: 'requestHl7Success', title: t('Istem (Teletip)'), width: 150,
                    render: (val, row) => (
                        <StatusTag
                            status={val}
                            sentAt={row.requestHl7SentAt}
                            error={row.requestHl7Error}
                            blockers={row.requestHl7Blockers}
                        />
                    )
                },
                {
                    key: 'reportHl7Success', dataIndex: 'reportHl7Success', title: t('Report (Teletıp)'), width: 150,
                    render: (val, row) => (
                        <StatusTag
                            status={val}
                            sentAt={row.reportHl7SentAt}
                            error={row.reportHl7Error}
                            blockers={row.reportHl7Blockers}
                        />
                    )
                },
                {
                    key: 'actions', title: t('Actions'), fixed: 'right', width: 270,
                    render: (_, row) => (
                        <Space size={4} wrap>
                            <Tooltip title={t('Force send RIS HL7 (skip all preconditions)')}>
                                <Button size="small" danger icon={<ThunderboltOutlined />} onClick={() => handleSend('risHl7', row)} />
                            </Tooltip>
                            <Tooltip title={t('Send RIS Teletip Job')}>
                                <Button size="small" icon={<ApiOutlined />} onClick={() => handleSend('ris', row)} />
                            </Tooltip>
                            <Tooltip title={t('Send Request HL7')}>
                                <Button size="small" icon={<SendOutlined />} onClick={() => handleSend('request', row)} />
                            </Tooltip>
                            <Tooltip title={t('Send Report HL7')}>
                                <Button size="small" icon={<FileTextOutlined />} onClick={() => handleSend('report', row)} />
                            </Tooltip>
                            <Tooltip title={t('HL7 Message Detail')}>
                                <Button
                                    size="small"
                                    icon={<EyeOutlined />}
                                    disabled={!row.requestHl7MessageId}
                                    onClick={() => setDetail({ open: true, messageId: row.requestHl7MessageId })}
                                />
                            </Tooltip>
                            <Tooltip title={t('Query Teletip Status')}>
                                <Button
                                    size="small"
                                    icon={<CloudSyncOutlined />}
                                    disabled={!row.accessionNumber}
                                    onClick={() => setTeletipStatus({
                                        open: true,
                                        appointmentId: row.appointmentId,
                                        accessionNumber: row.accessionNumber
                                    })}
                                />
                            </Tooltip>
                            <Tooltip title={t('Remove KOS')}>
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={!row.accessionNumber}
                                    onClick={() => setRemoveKos({ open: true, row })}
                                />
                            </Tooltip>
                        </Space>
                    )
                }
            ],
            antdTableProps: {
                scroll: { x: 1600 },
                size: 'middle'
            }
        })
    }, [])

    const load = async () => {
        const input = {
            ...queryModel,
            filter: {
                ...queryModel.filter,
                startDate: queryModel.filter?.startDate ? Helper.Date.beginOfDay(queryModel.filter.startDate) : null,
                endDate: queryModel.filter?.endDate ? Helper.Date.endOfDay(queryModel.filter.endDate) : null,
            }
        }
        const res = await ApiService.EnabizOperation.GetEnabizOperationDataGrid(input);
        if (res.success) {
            setData(res.data)
            setPaginationResult(res.pagination)
        }
    }

    useEffect(() => {
        load()
    }, [queryModel.updateKey])

    return (
        <>
            <DataGrid />
            <EnabizOperationDetailModal
                open={detail.open}
                messageId={detail.messageId}
                onClose={() => setDetail({ open: false, messageId: null })}
            />
            <EnabizTeletipStatusModal
                open={teletipStatus.open}
                appointmentId={teletipStatus.appointmentId}
                accessionNumber={teletipStatus.accessionNumber}
                onClose={() => setTeletipStatus({ open: false, appointmentId: null, accessionNumber: null })}
            />
            <EnabizRemoveKosModal
                open={removeKos.open}
                row={removeKos.row}
                onClose={() => setRemoveKos({ open: false, row: null })}
                onSuccess={refreshDataGrid}
            />
        </>
    )
}
